import React, { useState, useEffect } from 'react';
import { GitHubConfig as GitHubConfigType } from '../types';
import { useGitHubAuth } from '../hooks/useGitHubAuth';

interface GitHubConfigProps {
  onConfigSave: (config: GitHubConfigType) => void;
  initialConfig?: GitHubConfigType;
}

export function GitHubConfig({ onConfigSave, initialConfig }: GitHubConfigProps) {
  const { isAuthenticated, user, logout } = useGitHubAuth();
  const [owner, setOwner] = useState(initialConfig?.owner || user?.login || '');
  const [repo, setRepo] = useState(initialConfig?.repo || 'prompt-lib-a');
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    setIsValid(isAuthenticated && owner.length > 0 && repo.length > 0);
  }, [isAuthenticated, owner, repo]);

  useEffect(() => {
    if (user?.login && !owner) {
      setOwner(user.login);
    }
  }, [user, owner]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      // No token needed - handled by OAuth flow
      onConfigSave({ owner, repo });
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-center">Repository Configuration</h2>
      
      {isAuthenticated && user && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center space-x-3">
            <img src={user.avatar_url} alt={user.login} className="w-8 h-8 rounded-full" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Authenticated as {user.name || user.login}
              </p>
              <button
                onClick={logout}
                className="text-xs text-green-600 hover:text-green-800 underline"
              >
                Switch account
              </button>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">

        <div>
          <label htmlFor="owner" className="block text-sm font-medium text-gray-700 mb-1">
            Repository Owner
          </label>
          <input
            type="text"
            id="owner"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="username"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label htmlFor="repo" className="block text-sm font-medium text-gray-700 mb-1">
            Repository Name
          </label>
          <input
            type="text"
            id="repo"
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            placeholder="prompts"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <button
          type="submit"
          disabled={!isValid}
          className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          Connect to Repository
        </button>
      </form>
    </div>
  );
}